import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FiArrowLeft, FiEdit2, FiPlus, FiTrash2, FiX, FiCheck,
  FiCreditCard, FiFileText, FiDollarSign, FiInfo, FiTool, FiShoppingCart,
} from 'react-icons/fi';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { canStaffAccess } from '../utils/staffPages';
import toast from 'react-hot-toast';
import SettleVendorOpeningBalanceModal from '../components/admin/SettleVendorOpeningBalanceModal';

const money = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 };
const inputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '8px 10px',
  border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none',
};

const METHOD_LABELS = { cash: 'Cash', upi: 'UPI', bank_transfer: 'Bank Transfer', cheque: 'Cheque', other: 'Other' };
const STATUS_BADGE = {
  paid:      { background: '#dcfce7', color: '#16a34a' },
  pending:   { background: '#fef3c7', color: '#d97706' },
  cancelled: { background: '#f3f4f6', color: '#9ca3af' },
};

/* ─── Bank Account Modal ────────────────────────────────────────── */
const BankModal = ({ vendorId, account, onClose, onSaved }) => {
  const isEdit = Boolean(account?.id);
  const [form, setForm] = useState({
    account_holder_name: account?.account_holder_name || '',
    bank_name:           account?.bank_name || '',
    account_number:      account?.account_number || '',
    ifsc_code:           account?.ifsc_code || '',
    branch_name:         account?.branch_name || '',
    account_type:        account?.account_type || 'current',
    upi_id:              account?.upi_id || '',
    is_primary:          account?.is_primary || false,
    status:              account?.status || 'active',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.account_holder_name.trim()) return toast.error('Account holder name is required');
    const ifsc = form.ifsc_code.trim().toUpperCase();
    if (ifsc && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc)) {
      return toast.error('IFSC code must be 11 characters (e.g. HDFC0001234)');
    }
    try {
      setSaving(true);
      if (isEdit) {
        await API.put(`/vendors/bank-accounts/${account.id}`, form);
      } else {
        await API.post(`/vendors/${vendorId}/bank-accounts`, form);
      }
      toast.success(isEdit ? 'Account updated' : 'Account added');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 560, maxWidth: '95vw', boxShadow: '0 8px 40px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>{isEdit ? 'Edit Bank Account' : 'Add Bank Account'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><FiX size={18} /></button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
          {[
            { label: 'Account Holder Name *', field: 'account_holder_name', placeholder: 'As per bank records' },
            { label: 'Bank Name',             field: 'bank_name',           placeholder: 'e.g. HDFC Bank' },
            { label: 'Account Number',        field: 'account_number',      placeholder: '0000000000000' },
            { label: 'IFSC Code',             field: 'ifsc_code',           placeholder: 'HDFC0001234' },
            { label: 'Branch Name',           field: 'branch_name',         placeholder: 'e.g. Connaught Place' },
            { label: 'UPI ID',                field: 'upi_id',              placeholder: 'vendor@upi' },
          ].map(({ label, field, placeholder }) => (
            <div key={field}>
              <label style={labelStyle}>{label}</label>
              <input style={inputStyle} value={form[field]} onChange={e => set(field, e.target.value)} placeholder={placeholder} />
            </div>
          ))}
          <div>
            <label style={labelStyle}>Account Type</label>
            <select style={{ ...inputStyle, background: '#fff' }} value={form.account_type} onChange={e => set('account_type', e.target.value)}>
              <option value="savings">Savings</option>
              <option value="current">Current</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Status</label>
            <select style={{ ...inputStyle, background: '#fff' }} value={form.status} onChange={e => set('status', e.target.value)}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 20 }}>
            <input type="checkbox" id="is_primary" checked={form.is_primary && form.status === 'active'} disabled={form.status === 'inactive'} onChange={e => set('is_primary', e.target.checked)} />
            <label htmlFor="is_primary" style={{ fontSize: 14, cursor: 'pointer' }}>Set as primary account</label>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="crm-btn" onClick={onClose}>Cancel</button>
          <button className="crm-btn crm-btn--primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
};

/* ─── Payment Modal ─────────────────────────────────────────────── */
const PaymentModal = ({ vendorId, onClose, onSaved }) => {
  const [form, setForm] = useState({
    payment_date:     new Date().toISOString().split('T')[0],
    amount:           '',
    payment_method:   'cash',
    reference_number: '',
    notes:            '',
    status:           'paid',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.amount || Number(form.amount) <= 0) return toast.error('Enter a valid amount');
    if (!form.payment_date) return toast.error('Payment date is required');
    try {
      setSaving(true);
      await API.post(`/vendors/payments/create`, { ...form, vendor_id: vendorId, amount: Number(form.amount) });
      toast.success('Payment recorded');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 500, maxWidth: '95vw', boxShadow: '0 8px 40px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Record Payment</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><FiX size={18} /></button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
          <div>
            <label style={labelStyle}>Payment Date *</label>
            <input type="date" style={inputStyle} value={form.payment_date} onChange={e => set('payment_date', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Amount (₹) *</label>
            <input type="number" min="0" style={inputStyle} value={form.amount} onChange={e => set('amount', e.target.value)} placeholder="0.00" />
          </div>
          <div>
            <label style={labelStyle}>Payment Method</label>
            <select style={{ ...inputStyle, background: '#fff' }} value={form.payment_method} onChange={e => set('payment_method', e.target.value)}>
              {Object.entries(METHOD_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Reference / UTR No.</label>
            <input style={inputStyle} value={form.reference_number} onChange={e => set('reference_number', e.target.value)} placeholder="Optional" />
          </div>
          <div>
            <label style={labelStyle}>Status</label>
            <select style={{ ...inputStyle, background: '#fff' }} value={form.status} onChange={e => set('status', e.target.value)}>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
            </select>
          </div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Notes</label>
          <textarea style={{ ...inputStyle, minHeight: 70, resize: 'vertical', fontFamily: 'inherit' }} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="What was this payment for?" />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="crm-btn" onClick={onClose}>Cancel</button>
          <button className="crm-btn crm-btn--primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Record Payment'}</button>
        </div>
      </div>
    </div>
  );
};

/* ─── Services Tab ──────────────────────────────────────────────── */
const ServicesTab = ({ vendorId }) => {
  const [assigned, setAssigned]     = useState([]);
  const [allServices, setAllServices] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [adding, setAdding]         = useState(false);
  const [loading, setLoading]       = useState(true);

  const fetchAssigned = useCallback(async () => {
    try {
      const { data } = await API.get(`/vendors/${vendorId}/services`);
      setAssigned(data.services || []);
    } catch {
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  }, [vendorId]);

  const fetchAll = useCallback(async () => {
    const { data } = await API.get('/vendors/services/all');
    setAllServices(data.services || []);
  }, []);

  useEffect(() => { fetchAssigned(); fetchAll(); }, [fetchAssigned, fetchAll]);

  // Services not yet assigned
  const assignedIds = new Set(assigned.map(s => s.id));
  const available   = allServices.filter(s => !assignedIds.has(s.id));

  const handleAdd = async () => {
    if (!selectedId) return toast.error('Select a service first');
    try {
      setAdding(true);
      await API.post(`/vendors/${vendorId}/services`, { service_id: Number(selectedId) });
      toast.success('Service assigned');
      setSelectedId('');
      fetchAssigned();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to assign');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (serviceId, name) => {
    if (!window.confirm(`Remove "${name}" from this vendor?`)) return;
    try {
      await API.delete(`/vendors/${vendorId}/services/${serviceId}`);
      toast.success('Service removed');
      fetchAssigned();
    } catch (err) {
      toast.error('Failed to remove');
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>;

  return (
    <div>
      {/* Assign row */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, alignItems: 'center' }}>
        <select
          value={selectedId}
          onChange={e => setSelectedId(e.target.value)}
          style={{ flex: 1, padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, background: '#fff' }}
        >
          <option value="">— Select a service to assign —</option>
          {available.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <button
          className="crm-btn crm-btn--primary"
          onClick={handleAdd}
          disabled={adding || !selectedId}
          style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}
        >
          <FiPlus size={14} /> {adding ? 'Assigning…' : 'Assign Service'}
        </button>
      </div>

      {/* Assigned services */}
      {assigned.length === 0 ? (
        <div className="crm-card" style={{ textAlign: 'center', padding: 48, color: '#9ca3af' }}>
          <FiTool size={32} style={{ marginBottom: 10, opacity: 0.35 }} />
          <p style={{ margin: 0 }}>No services assigned to this vendor yet.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {assigned.map(s => (
            <div key={s.id} className="crm-card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{s.name}</div>
                {s.description && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{s.description}</div>}
              </div>
              <button
                onClick={() => handleRemove(s.id, s.name)}
                title="Remove service"
                style={{ background: '#fee2e2', border: 'none', borderRadius: 6, padding: '5px 8px', cursor: 'pointer', flexShrink: 0 }}
              >
                <FiTrash2 size={13} color="#dc2626" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─── Main Component ────────────────────────────────────────────── */
export default function AdminVendorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || canStaffAccess(user?.allowedPages, 'vendors', 'edit');
  const canManageBank = user?.role === 'admin';   // backend allows only admin for bank accounts
  const [vendor, setVendor]       = useState(null);
  const [payments, setPayments]   = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [tab, setTab]             = useState('overview');
  const [bankModal, setBankModal] = useState(null);
  const [payModal, setPayModal]   = useState(false);
  const [obSummary, setObSummary] = useState(null);     // opening balance settled / pending
  const [obModal, setObModal]     = useState(false);
  const isAdmin = user?.role === 'admin';                // backend allows only admin to create/cancel payments

  const fetchVendor = useCallback(async () => {
    try {
      const { data } = await API.get(`/vendors/${id}`);
      setVendor(data.vendor);
    } catch (err) {
      toast.error('Failed to load vendor');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchPayments = useCallback(async () => {
    const { data } = await API.get(`/vendors/${id}/payments`);
    setPayments(data.payments || []);
  }, [id]);

  const fetchPurchases = useCallback(async () => {
    try {
      const { data } = await API.get(`/vendors/${id}/purchases`, { params: { limit: 100 } });
      setPurchases(data.purchases || []);
    } catch {}
  }, [id]);

  const fetchObSummary = useCallback(async () => {
    try {
      const { data } = await API.get(`/vendors/${id}/opening-balance`);
      setObSummary(data.openingBalance || null);
    } catch {
      setObSummary(null);   // the page still works without the settlement figures
    }
  }, [id]);

  useEffect(() => { fetchVendor(); fetchPayments(); fetchPurchases(); fetchObSummary(); }, [fetchVendor, fetchPayments, fetchPurchases, fetchObSummary]);

  const handleDeleteBank = async (accountId) => {
    if (!window.confirm('Delete this bank account?')) return;
    try {
      await API.delete(`/vendors/bank-accounts/${accountId}`);
      toast.success('Deleted');
      fetchVendor();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    }
  };

  const handleCancelPayment = async (paymentId) => {
    if (!window.confirm('Cancel this payment?')) return;
    try {
      await API.put(`/vendors/payments/${paymentId}`, { status: 'cancelled' });
      toast.success('Payment cancelled');
      fetchPayments();
      fetchVendor();
      fetchObSummary();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    }
  };

  const handleMarkPaid = async (paymentId) => {
    try {
      await API.put(`/vendors/payments/${paymentId}`, { status: 'paid' });
      toast.success('Marked as paid');
      fetchPayments();
      fetchVendor();
    } catch (err) {
      toast.error('Failed');
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>;
  if (!vendor) return <div style={{ padding: 40, textAlign: 'center', color: '#dc2626' }}>Vendor not found</div>;

  const totalPaid      = payments.filter(p => p.status === 'paid').reduce((s, p) => s + Number(p.amount), 0);
  const totalPurchases = purchases.filter(p => p.status !== 'CANCELLED').reduce((s, p) => s + Number(p.grand_total), 0);
  const totalAllocated = purchases.reduce((s, p) => s + Number(p.total_allocated || 0), 0);
  const opening        = Number(vendor.opening_balance || 0);
  // base: 'debit' = you owe them (+), 'credit' = they owe you (-)
  // outstanding = base + total_purchases - total_allocated_payments
  const base           = vendor.opening_balance_type === 'debit' ? opening : -opening;
  const outstanding    = base + totalPurchases - totalPaid;

  const serviceCount = (vendor.services || []).length;

  // Vendor type badge color
  const typeColors = {
    SUPPLIER:  { bg: '#f0fdf4', color: '#16a34a' },
    OUTSOURCE: { bg: '#eff6ff', color: '#2563eb' },
    BOTH:      { bg: '#faf5ff', color: '#7c3aed' },
  };
  const typeName   = vendor.vendorType?.name || '';
  const typeStyle  = typeColors[typeName] || { bg: '#f3f4f6', color: '#6b7280' };

  const TABS = [
    { key: 'overview',  label: 'Overview',      icon: <FiInfo size={14} /> },
    { key: 'services',  label: 'Services',       icon: <FiTool size={14} /> },
    { key: 'purchases', label: 'Purchases',      icon: <FiShoppingCart size={14} /> },
    { key: 'payments',  label: 'Payments',       icon: <FiDollarSign size={14} /> },
    { key: 'banks',     label: 'Bank Accounts',  icon: <FiCreditCard size={14} /> },
  ];

  return (
    <div style={{ padding: '28px 24px' }}>
      {/* Modals */}
      {bankModal !== null && (
        <BankModal
          vendorId={id}
          account={bankModal === 'new' ? null : bankModal}
          onClose={() => setBankModal(null)}
          onSaved={() => { setBankModal(null); fetchVendor(); }}
        />
      )}
      {obModal && obSummary && (
        <SettleVendorOpeningBalanceModal
          vendor={vendor}
          summary={obSummary}
          onClose={() => setObModal(false)}
          onSaved={() => { fetchPayments(); fetchVendor(); fetchObSummary(); }}
        />
      )}
      {payModal && (
        <PaymentModal
          vendorId={id}
          onClose={() => setPayModal(false)}
          onSaved={() => { setPayModal(false); fetchPayments(); fetchVendor(); }}
        />
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => navigate('/admin/vendors')} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '7px 10px', cursor: 'pointer' }}>
            <FiArrowLeft size={16} />
          </button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, fontWeight: 700, fontSize: '1.3rem' }}>{vendor.company_name}</h2>
              <span style={{ fontFamily: 'monospace', fontSize: 13, color: '#6366f1', background: '#eef2ff', padding: '2px 8px', borderRadius: 6 }}>{vendor.vendor_code}</span>
              <span style={{
                padding: '2px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600,
                background: vendor.status === 'active' ? '#dcfce7' : '#f3f4f6',
                color:      vendor.status === 'active' ? '#16a34a' : '#6b7280',
              }}>{vendor.status}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
              {typeName && (
                <span style={{ fontSize: 12, fontWeight: 700, padding: '2px 10px', borderRadius: 99, background: typeStyle.bg, color: typeStyle.color }}>
                  {typeName}
                </span>
              )}
              {serviceCount > 0 && (
                <span style={{ fontSize: 12, color: '#9ca3af' }}>
                  {serviceCount} service{serviceCount !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
        </div>
        {canEdit && <button className="crm-btn" onClick={() => navigate(`/admin/vendors/${id}/edit`)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <FiEdit2 size={14} /> Edit
        </button>}
      </div>

      {/* Stats bar */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
        {[
          { label: 'Outstanding Balance', value: outstanding === 0 ? '—' : money(Math.abs(outstanding)), color: outstanding > 0 ? '#dc2626' : outstanding < 0 ? '#16a34a' : '#374151', note: outstanding > 0 ? 'You owe them' : outstanding < 0 ? 'They owe you' : 'Settled' },
          { label: 'Total Purchases',     value: money(totalPurchases), color: '#374151', note: `${purchases.filter(p => p.status !== 'CANCELLED').length} purchase(s)` },
          { label: 'Total Allocated',     value: money(totalAllocated), color: '#16a34a', note: 'Payments applied' },
          { label: 'Opening Balance',     value: money(vendor.opening_balance), color: '#374151', note: vendor.opening_balance_type === 'debit' ? 'Debit (you owe)' : 'Credit (they owe)' },
        ].map(s => (
          <div key={s.label} className="crm-card" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>{s.note}</div>
          </div>
        ))}
      </div>

      {/* Opening balance settlement (only when we owe the vendor an opening balance) */}
      {obSummary && obSummary.type === 'debit' && obSummary.amount > 0 && (
        <div className="crm-card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
          <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Opening Balance (you owe)</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{money(obSummary.amount)}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Paid</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#16a34a' }}>{money(obSummary.settled)}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Pending</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: obSummary.remaining > 0 ? '#dc2626' : '#16a34a' }}>
                {obSummary.remaining > 0 ? money(obSummary.remaining) : 'Fully settled ✓'}
              </div>
            </div>
          </div>
          {isAdmin && obSummary.canSettle && (
            <button className="crm-btn crm-btn--primary" onClick={() => setObModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <FiCheck size={14} /> Settle Opening Balance
            </button>
          )}
          {/* Settle button hidden because the date is missing — say so instead of failing silently */}
          {isAdmin && !obSummary.canSettle && !obSummary.date && obSummary.remaining > 0 && (
            <div style={{ maxWidth: 340, padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 12.5, color: '#92400e', lineHeight: 1.5 }}>
              <strong>Opening balance date is missing.</strong> Set it to enable settlement:
              go to <strong>Ledger → Vendor → {vendor.company_name} → Opening Bal</strong> and enter the date.
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '2px solid #f3f4f6' }}>
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer',
              fontSize: 14, fontWeight: tab === t.key ? 700 : 500,
              color: tab === t.key ? '#6366f1' : '#6b7280',
              borderBottom: tab === t.key ? '2px solid #6366f1' : '2px solid transparent',
              marginBottom: -2,
            }}
          >
            {t.icon} {t.label}
            {t.badge ? (
              <span style={{ background: '#6366f1', color: '#fff', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 99 }}>
                {t.badge}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {/* ── Tab: Overview ── */}
      {tab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <div className="crm-card">
            <div style={{ fontWeight: 700, fontSize: 13, color: '#374151', marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Contact</div>
            {[
              { label: 'Contact Person', value: vendor.contact_person },
              { label: 'Phone',          value: vendor.phone },
              { label: 'Alternate Phone',value: vendor.alternate_phone },
              { label: 'Email',          value: vendor.email },
            ].map(r => (
              <div key={r.label} style={{ display: 'flex', gap: 8, marginBottom: 10, fontSize: 14 }}>
                <span style={{ color: '#9ca3af', minWidth: 130 }}>{r.label}</span>
                <span style={{ fontWeight: 500 }}>{r.value || '—'}</span>
              </div>
            ))}
          </div>
          <div className="crm-card">
            <div style={{ fontWeight: 700, fontSize: 13, color: '#374151', marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Tax & Compliance</div>
            {[
              { label: 'GSTIN',          value: vendor.gstin },
              { label: 'PAN',            value: vendor.pan },
              { label: 'MSME',           value: vendor.msme },
              { label: 'Payment Terms',  value: vendor.payment_terms_days ? `${vendor.payment_terms_days} days` : '—' },
            ].map(r => (
              <div key={r.label} style={{ display: 'flex', gap: 8, marginBottom: 10, fontSize: 14 }}>
                <span style={{ color: '#9ca3af', minWidth: 130 }}>{r.label}</span>
                <span style={{ fontWeight: 500 }}>{r.value || '—'}</span>
              </div>
            ))}
          </div>
          <div className="crm-card">
            <div style={{ fontWeight: 700, fontSize: 13, color: '#374151', marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Address</div>
            <div style={{ fontSize: 14, lineHeight: 1.8, color: '#374151' }}>
              {[vendor.address_line1, vendor.address_line2, vendor.city, vendor.state, vendor.pincode, vendor.country].filter(Boolean).join(', ') || '—'}
            </div>
          </div>
          <div className="crm-card">
            <div style={{ fontWeight: 700, fontSize: 13, color: '#374151', marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Notes</div>
            <div style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.7 }}>{vendor.notes || 'No notes added.'}</div>
          </div>
        </div>
      )}

      {/* ── Tab: Services ── */}
      {tab === 'services' && <ServicesTab vendorId={id} />}

      {/* ── Tab: Bank Accounts ── */}
      {tab === 'banks' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            {canManageBank && (
              <button className="crm-btn crm-btn--primary" onClick={() => setBankModal('new')} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <FiPlus size={14} /> Add Bank Account
              </button>
            )}
          </div>
          {(vendor.bankAccounts || []).length === 0 ? (
            <div className="crm-card" style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>No bank accounts added yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {vendor.bankAccounts.map(acc => (
                <div key={acc.id} className="crm-card" style={{ border: acc.is_primary ? '1.5px solid #6366f1' : undefined }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontWeight: 700, fontSize: 15 }}>{acc.bank_name || 'Bank Account'}</span>
                      {acc.is_primary && <span style={{ background: '#eef2ff', color: '#6366f1', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99 }}>Primary</span>}
                      <span style={{ background: acc.status === 'active' ? '#dcfce7' : '#f3f4f6', color: acc.status === 'active' ? '#16a34a' : '#9ca3af', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99 }}>{acc.status}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {canManageBank && <button onClick={() => setBankModal(acc)} style={{ background: '#f3f4f6', border: 'none', borderRadius: 6, padding: '5px 8px', cursor: 'pointer' }}><FiEdit2 size={13} /></button>}
                      {canManageBank && <button onClick={() => handleDeleteBank(acc.id)} style={{ background: '#fee2e2', border: 'none', borderRadius: 6, padding: '5px 8px', cursor: 'pointer' }}><FiTrash2 size={13} color="#dc2626" /></button>}
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, fontSize: 13 }}>
                    {[
                      { label: 'Holder',     value: acc.account_holder_name },
                      { label: 'Account No.', value: acc.account_number },
                      { label: 'IFSC',       value: acc.ifsc_code },
                      { label: 'Branch',     value: acc.branch_name },
                      { label: 'Type',       value: acc.account_type },
                      { label: 'UPI',        value: acc.upi_id },
                    ].map(r => (
                      <div key={r.label}>
                        <div style={{ color: '#9ca3af', fontSize: 11, marginBottom: 2 }}>{r.label}</div>
                        <div style={{ fontWeight: 600 }}>{r.value || '—'}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Payments ── */}
      {tab === 'payments' && (
        <div>
          {/* <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <button className="crm-btn crm-btn--primary" onClick={() => setPayModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {canEdit && <><FiPlus size={14} /> Record Payment</>}
            </button>
          </div> */}
          {payments.length === 0 ? (
            <div className="crm-card" style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>No payments recorded yet.</div>
          ) : (
            <div className="crm-card" style={{ padding: 0, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #f3f4f6', background: '#fafafa' }}>
                    {['Payment No.', 'Date', 'Amount', 'Method', 'Reference', 'Status', ''].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '10px 14px', fontWeight: 600, fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {payments.map(p => (
                    <tr key={p.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '11px 14px', fontFamily: 'monospace', fontSize: 13, color: '#6366f1', fontWeight: 600 }}>
                        {p.payment_number}
                        {Number(p.ob_allocated_amount) > 0 && (
                          <span style={{ marginLeft: 8, fontFamily: 'inherit', fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 99, background: '#fef3c7', color: '#b45309' }}>Opening Balance</span>
                        )}
                      </td>
                      <td style={{ padding: '11px 14px', color: '#374151' }}>{fmt(p.payment_date)}</td>
                      <td style={{ padding: '11px 14px', fontWeight: 700 }}>{money(p.amount)}</td>
                      <td style={{ padding: '11px 14px', color: '#6b7280' }}>{METHOD_LABELS[p.payment_method] || p.payment_method}</td>
                      <td style={{ padding: '11px 14px', color: '#6b7280' }}>{p.reference_number || '—'}</td>
                      <td style={{ padding: '11px 14px' }}>
                        <span style={{ ...STATUS_BADGE[p.status], padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600 }}>{p.status}</span>
                      </td>
                      <td style={{ padding: '11px 14px' }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {p.status === 'pending' && (
                            <button onClick={() => handleMarkPaid(p.id)} title="Mark Paid" style={{ background: '#dcfce7', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer' }}>
                              <FiCheck size={13} color="#16a34a" />
                            </button>
                          )}
                          {isAdmin && p.status === 'paid' && Number(p.ob_allocated_amount) > 0 && (
                            <button onClick={() => handleCancelPayment(p.id)} title="Cancel opening balance payment" style={{ background: '#fee2e2', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer' }}>
                              <FiX size={13} color="#dc2626" />
                            </button>
                          )}
                          {p.status !== 'cancelled' && p.status !== 'paid' && (
                            <button onClick={() => handleCancelPayment(p.id)} title="Cancel" style={{ background: '#fee2e2', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer' }}>
                              <FiX size={13} color="#dc2626" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Purchases ── */}
      {tab === 'purchases' && (
        <div>
          {/* <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <button
              className="crm-btn crm-btn--primary"
              onClick={() => navigate('/admin/purchases/new')}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              {canEdit && <><FiPlus size={14} /> New Purchase</>}
            </button>
          </div> */}
          {purchases.length === 0 ? (
            <div className="crm-card" style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
              <FiShoppingCart size={32} style={{ marginBottom: 10, opacity: 0.4 }} />
              <p>No purchases recorded for this vendor.</p>
            </div>
          ) : (
            <div className="crm-card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #f3f4f6', background: '#fafafa' }}>
                      {['Purchase No.', 'Date', 'Type', 'Invoice No.', 'Total', 'Paid', 'Outstanding', 'Status'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '10px 14px', fontWeight: 600, fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {purchases.map(pur => {
                      const PUR_STATUS = {
                        DRAFT:     { background: '#fef3c7', color: '#d97706' },
                        CONFIRMED: { background: '#dcfce7', color: '#16a34a' },
                        CANCELLED: { background: '#f3f4f6', color: '#9ca3af' },
                      };
                      const TYPE_COL = {
                        MATERIAL:    { background: '#eff6ff', color: '#2563eb' },
                        OUTSOURCING: { background: '#faf5ff', color: '#7c3aed' },
                        OTHER:       { background: '#f3f4f6', color: '#6b7280' },
                      };
                      return (
                        <tr
                          key={pur.id}
                          onClick={() => navigate(`/admin/purchases/${pur.id}`)}
                          style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                          onMouseLeave={e => e.currentTarget.style.background = ''}
                        >
                          <td style={{ padding: '11px 14px', fontFamily: 'monospace', fontSize: 13, color: '#6366f1', fontWeight: 600 }}>{pur.purchase_number}</td>
                          <td style={{ padding: '11px 14px', color: '#374151', whiteSpace: 'nowrap' }}>{fmt(pur.purchase_date)}</td>
                          <td style={{ padding: '11px 14px' }}>
                            <span style={{ ...TYPE_COL[pur.purchase_type], padding: '2px 8px', borderRadius: 99, fontSize: 12, fontWeight: 600 }}>{pur.purchase_type}</span>
                          </td>
                          <td style={{ padding: '11px 14px', color: '#6b7280', fontFamily: 'monospace', fontSize: 13 }}>{pur.vendor_invoice_number || '—'}</td>
                          <td style={{ padding: '11px 14px', fontWeight: 700 }}>{money(pur.grand_total)}</td>
                          <td style={{ padding: '11px 14px', color: '#16a34a', fontWeight: 600 }}>{money(pur.total_allocated)}</td>
                          <td style={{ padding: '11px 14px', fontWeight: 600, color: pur.outstanding > 0 ? '#dc2626' : '#16a34a' }}>{money(pur.outstanding)}</td>
                          <td style={{ padding: '11px 14px' }}>
                            <span style={{ ...PUR_STATUS[pur.status], padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600 }}>{pur.status}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}